import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api';
import { useDebounced, useListState } from '../../lib/hooks';
import { date, money, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { DateRangeFilter } from '../../components/DateRangeFilter';
import { CodeText } from '../../components/Code';
import { ExportMenu } from '../../components/ExportMenu';
import { FxRateField } from '../../components/FxRateField';
import { currencySymbol, fxMoney, isForeignCurrency, toBase } from '../../lib/currency';
import {
  exportDay,
  exportNotes,
  exportNum,
  exportPeriod,
  fetchAllRows,
  type TableExportDoc,
} from '../../lib/tableExport';
import {
  describeFilters,
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from '../../components/ColumnFilter';
import {
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  Pager,
  SearchBox,
  Stat,
  TableSkeleton,
} from '../../components/ui';

interface ExpenseRow {
  id: string;
  number: string;
  date: string;
  amount: string;
  taxAmount: string;
  description: string;
  reference: string | null;
  accountId: string;
  cashAccountId: string;
  supplierId: string | null;
  billableAccountId: string | null;
  /** إجمالي المصروف بعملة الخزينة الأجنبية وسعره. */
  fxCurrency?: string | null;
  fxAmount?: string | null;
  fxRate?: string | null;
  account: { id: string; code: string; name: string };
  cashAccount: { id: string; name: string };
  supplier: { id: string; name: string } | null;
}

interface ExpensePage {
  items: ExpenseRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  totals: { amount: number; taxAmount: number; gross: number };
}

interface Option {
  id: string;
  code?: string;
  name: string;
  currency?: string;
}

/** أعمدة الجدول بمفاتيحها — المفتاح نفسه مفتاح الفلتر في رأس العمود. */
const EXPENSE_COLUMNS = [
  { key: 'number', label: 'الرقم', kind: 'code' },
  { key: 'date', label: 'التاريخ' },
  { key: 'description', label: 'البيان', minWidth: 24 },
  { key: 'account', label: 'الحساب' },
  { key: 'cashAccount', label: 'مدفوع من' },
  { key: 'supplier', label: 'المورد' },
  { key: 'amount', label: 'القيمة' },
] as const;

/** أعمدة زرّ «فلتر» — تقابل `FILTERABLE` في expenses.service.ts. */
const EXPENSE_FILTERS: FilterDef[] = [
  { key: 'number', label: 'الرقم', kind: 'text', sortable: true },
  { key: 'date', label: 'التاريخ', kind: 'date', sortable: true },
  { key: 'description', label: 'البيان', kind: 'text', noList: true },
  { key: 'account', label: 'الحساب', kind: 'text' },
  { key: 'cashAccount', label: 'مدفوع من', kind: 'text' },
  { key: 'supplier', label: 'المورد', kind: 'text' },
  { key: 'amount', label: 'القيمة', kind: 'number', sortable: true },
];

export function ExpensesList() {
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);
  const [search, setSearch] = useState(state.q ?? '');
  const debounced = useDebounced(search);
  const [editing, setEditing] = useState<ExpenseRow | 'new' | null>(null);
  const [breakdown, setBreakdown] = useState(false);

  const from = state.from ?? '';
  const to = state.to ?? '';
  const canEdit = atLeast('MANAGER');
  const query = { ...state, q: debounced || undefined, page };

  const list = useQuery({
    queryKey: ['expenses', query],
    queryFn: () => api<ExpensePage>(`/finance/expenses${qs(query)}`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['expenses'] });
    void queryClient.invalidateQueries({ queryKey: ['cash-accounts'] });
    setEditing(null);
  };

  /* التصدير: كل المصروفات المطابقة للبحث والمدى والفلاتر، لا الصفحة المعروضة وحدها. */
  const buildExport = async (): Promise<TableExportDoc> => {
    const { items, total, truncated } = await fetchAllRows<ExpenseRow>('/finance/expenses', query, 100);
    return {
      title: 'المصروفات',
      sheets: [
        {
          title: 'المصروفات',
          period: exportPeriod(from, to),
          sections: [
            {
              columns: [
                { key: 'number', label: 'الرقم', kind: 'code' },
                { key: 'date', label: 'التاريخ', kind: 'date' },
                { key: 'description', label: 'البيان', minWidth: 24 },
                { key: 'account', label: 'الحساب' },
                { key: 'cashAccount', label: 'مدفوع من' },
                { key: 'supplier', label: 'المورد' },
                { key: 'reference', label: 'المرجع' },
                { key: 'taxAmount', label: 'الضريبة', kind: 'money', total: true },
                { key: 'amount', label: 'القيمة', kind: 'money', total: true },
              ],
              rows: items.map((e) => ({
                number: e.number,
                date: exportDay(e.date),
                description: e.description,
                account: `${e.account.code} ${e.account.name}`,
                cashAccount: e.cashAccount.name,
                supplier: e.supplier?.name ?? null,
                reference: e.reference,
                taxAmount: exportNum(e.taxAmount),
                amount: exportNum(e.amount),
              })),
            },
          ],
          notes: exportNotes({
            q: debounced,
            conditions: describeFilters(EXPENSE_FILTERS, filters),
            count: items.length,
            total,
            truncated,
          }),
        },
      ],
    };
  };

  return (
    <div className="page">
      <PageHead
        title="المصروفات"
        subtitle="ما يُدفع فورًا دون فاتورة مورد مستحقة"
        actions={
          <>
            <button className="btn" onClick={() => setBreakdown(true)}>
              <Icon name="chartBar" size={15} />
              التوزيع بالحساب
            </button>
            {canEdit && (
              <button className="btn btn-primary" onClick={() => setEditing('new')}>
                <Icon name="plus" size={15} />
                مصروف جديد
              </button>
            )}
          </>
        }
      />

      <div className="stats">
        <Stat
          label="عدد المصروفات"
          value={list.data ? list.data.total : '—'}
          note={from || to ? 'ضمن الفترة المحددة' : 'كل الفترات'}
        />
        <Stat
          label="الإجمالي"
          value={<span className="num">{list.data ? money(list.data.totals.amount) : '—'}</span>}
        />
        <Stat
          label="الضريبة"
          value={<span className="num">{list.data ? money(list.data.totals.taxAmount) : '—'}</span>}
        />
      </div>

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بالرقم أو الوصف أو المرجع"
        />

        <DateRangeFilter from={from} to={to} onChange={(f, t) => patch({ from: f, to: t })} />

        <ExportMenu build={buildExport} disabled={!list.data?.total} />

        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton defs={EXPENSE_FILTERS} filters={filters} onChange={setFilters} />
        </span>
      </div>

      <FilterChips defs={EXPENSE_FILTERS} filters={filters} onChange={setFilters} />

      <FilterScope
        defs={EXPENSE_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/finance/expenses/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                {EXPENSE_COLUMNS.map((c) => (
                  <th key={c.key}>
                    {c.label}
                    <HeaderFilter colKey={c.key} />
                  </th>
                ))}
                {canEdit && <th />}
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={canEdit ? 8 : 7} />}

            {list.data && (
              <tbody>
                {list.data.items.map((e) => (
                  <tr key={e.id}>
                    <td className="num cell-mute">{e.number}</td>
                    <td className="num cell-mute">{date(e.date)}</td>
                    <td className="cell-strong">
                      <CodeText text={e.description} />
                    </td>
                    <td className="cell-mute">
                      <span className="num">{e.account.code}</span> {e.account.name}
                    </td>
                    <td className="cell-mute">{e.cashAccount.name}</td>
                    <td className="cell-mute">{e.supplier?.name ?? '—'}</td>
                    <td className="num cell-strong">{money(e.amount)}</td>
                    {canEdit && (
                      <td className="end">
                        <button
                          className="btn btn-sm btn-icon btn-ghost"
                          onClick={() => setEditing(e)}
                          aria-label={`تعديل ${e.number}`}
                        >
                          <Icon name="edit" size={15} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

        {list.data?.items.length === 0 && (
          <EmptyState
            title="لا مصروفات"
            text="سجّل مصروفًا ليُرحَّل إلى الدفاتر فورًا ويظهر أثره في الخزينة وفي قائمة الدخل."
            action={
              canEdit && (
                <button className="btn btn-primary" onClick={() => setEditing('new')}>
                  مصروف جديد
                </button>
              )
            }
          />
        )}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
            onPageSize={(n) => patch({ pageSize: String(n), page: undefined })}
          />
        )}
      </div>
      </FilterScope>

      {editing && (
        <ExpenseModal
          expense={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}

      {breakdown && (
        <BreakdownModal from={from} to={to} onClose={() => setBreakdown(false)} />
      )}
    </div>
  );
}

/* ========================================================================== */

function ExpenseModal({
  expense,
  onClose,
  onSaved,
}: {
  expense: ExpenseRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  /*
   * مصروف محفوظ من خزينة أجنبية يُفتح بمبالغه بعملتها: القيمة مقابلها
   * بالجنيه على السعر، والضريبة ما يبقى من الإجمالي بالعملة.
   */
  const savedRate = Number(expense?.fxRate ?? 0);
  const savedFx = expense?.fxAmount != null && savedRate > 0;
  const savedNet = savedFx ? Math.round((Number(expense!.amount) / savedRate) * 100) / 100 : 0;
  const [form, setForm] = useState({
    date: expense ? expense.date.slice(0, 10) : today,
    accountId: expense?.accountId ?? '',
    cashAccountId: expense?.cashAccountId ?? '',
    supplierId: expense?.supplierId ?? '',
    amount: expense ? String(savedFx ? savedNet : Number(expense.amount)) : '',
    taxAmount: expense
      ? String(
          savedFx
            ? Math.round((Number(expense.fxAmount) - savedNet) * 100) / 100
            : Number(expense.taxAmount),
        )
      : '0',
    fxRate: savedFx ? String(savedRate) : '',
    description: expense?.description ?? '',
    reference: expense?.reference ?? '',
    billableAccountId: expense?.billableAccountId ?? '',
  });

  const set = (key: string, value: unknown) => setForm((f) => ({ ...f, [key]: value }));

  const accounts = useQuery({
    queryKey: ['ledger', 'expenses'],
    queryFn: () => api<Option[]>('/accounting/accounts/postable?type=EXPENSE'),
  });
  const cashAccounts = useQuery({
    queryKey: ['cash-accounts', false],
    queryFn: () => api<{ items: Option[] }>('/finance/cash-accounts'),
  });
  const suppliers = useQuery({
    queryKey: ['suppliers', 'options'],
    queryFn: () => api<{ items: Option[] }>('/finance/suppliers?pageSize=100&isActive=true'),
  });
  const clients = useQuery({
    queryKey: ['accounts', 'options'],
    queryFn: () => api<{ items: Option[] }>('/crm/accounts?pageSize=100&sort=name&order=asc'),
  });

  // خزينة بعملة أجنبية: القيمة والضريبة تُدخلان بعملتها مع السعر.
  const cashCurrency =
    cashAccounts.data?.items.find((c) => c.id === form.cashAccountId)?.currency ?? 'EGP';
  const foreign = isForeignCurrency(cashCurrency);
  const fxNet = Number(form.amount) || 0;
  const fxTax = Number(form.taxAmount) || 0;
  const rate = Number(form.fxRate) || 0;

  const save = useMutation({
    mutationFn: () => {
      const baseNet = foreign ? toBase(fxNet, rate) : fxNet;
      const baseTotal = foreign ? toBase(fxNet + fxTax, rate) : fxNet + fxTax;
      const body = {
        date: form.date,
        accountId: form.accountId,
        cashAccountId: form.cashAccountId,
        supplierId: form.supplierId || null,
        amount: baseNet,
        taxAmount: Math.round((baseTotal - baseNet) * 100) / 100,
        ...(foreign ? { fxAmount: fxNet, fxTax, fxRate: rate } : {}),
        description: form.description,
        reference: form.reference || undefined,
        billableAccountId: form.billableAccountId || null,
      };
      return expense
        ? api(`/finance/expenses/${expense.id}`, { method: 'PATCH', body })
        : api('/finance/expenses', { method: 'POST', body });
    },
    onSuccess: onSaved,
  });

  const remove = useMutation({
    mutationFn: () =>
      api(`/finance/expenses/${expense!.id}${qs({ reason: 'حذف من الشاشة' })}`, {
        method: 'DELETE',
      }),
    onSuccess: onSaved,
  });

  const valid =
    form.accountId &&
    form.cashAccountId &&
    Number(form.amount) > 0 &&
    form.description.length >= 2 &&
    (!foreign || rate > 0);

  return (
    <Modal
      title={expense ? `تعديل ${expense.number}` : 'مصروف جديد'}
      onClose={onClose}
      footer={
        <>
          {expense && (
            <button
              className="btn btn-danger me-auto"
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              حذف
            </button>
          )}
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      {expense && (
        <div className="alert alert-info mb-4">
          تعديل مصروف مرحَّل يعكس قيده ويعيد ترحيله، فيبقى في الدفتر أثر ما كان قبل التعديل.
        </div>
      )}

      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="ex-desc">
            البيان
          </label>
          <input
            id="ex-desc"
            className="input"
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            autoFocus
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="ex-date">
            التاريخ
          </label>
          <input
            id="ex-date"
            className="input"
            type="date"
            value={form.date}
            onChange={(e) => set('date', e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="ex-amount">
            القيمة{foreign ? ` (${currencySymbol(cashCurrency)})` : ''}
          </label>
          <input
            id="ex-amount"
            className="input num"
            type="number"
            min="0"
            step="any"
            value={form.amount}
            onChange={(e) => set('amount', e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="ex-account">
            حساب المصروف
          </label>
          <select
            id="ex-account"
            className="select"
            value={form.accountId}
            onChange={(e) => set('accountId', e.target.value)}
          >
            <option value="">اختر حسابًا</option>
            {accounts.data?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} — {a.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label req" htmlFor="ex-cash">
            مدفوع من
          </label>
          <select
            id="ex-cash"
            className="select"
            value={form.cashAccountId}
            onChange={(e) => set('cashAccountId', e.target.value)}
          >
            <option value="">اختر خزينة</option>
            {cashAccounts.data?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {isForeignCurrency(c.currency) ? ` (${c.currency})` : ''}
              </option>
            ))}
          </select>
          {foreign && (
            <span className="tiny mute">
              خزينة بالـ {cashCurrency}: أدخل القيمة والضريبة بعملتها — الإجمالي{' '}
              {fxMoney(fxNet + fxTax, cashCurrency)}.
            </span>
          )}
        </div>

        {foreign && (
          <FxRateField
            id="ex-rate"
            currency={cashCurrency}
            date={form.date}
            rate={form.fxRate}
            onRate={(v) => set('fxRate', v)}
            fxTotal={fxNet + fxTax}
            keepInitial={savedFx}
          />
        )}

        <div className="field">
          <label className="label" htmlFor="ex-tax">
            الضريبة{foreign ? ` (${currencySymbol(cashCurrency)})` : ''}
          </label>
          <input
            id="ex-tax"
            className="input num"
            type="number"
            min="0"
            step="any"
            value={form.taxAmount}
            onChange={(e) => set('taxAmount', e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ex-ref">
            المرجع
          </label>
          <input
            id="ex-ref"
            className="input"
            value={form.reference}
            onChange={(e) => set('reference', e.target.value)}
            placeholder="رقم إيصال أو شيك"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ex-supplier">
            المورد
          </label>
          <select
            id="ex-supplier"
            className="select"
            value={form.supplierId}
            onChange={(e) => set('supplierId', e.target.value)}
          >
            <option value="">بدون</option>
            {suppliers.data?.items.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="ex-billable">
            يخص عميلًا
          </label>
          <select
            id="ex-billable"
            className="select"
            value={form.billableAccountId}
            onChange={(e) => set('billableAccountId', e.target.value)}
          >
            <option value="">بدون</option>
            {clients.data?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <span className="tiny mute">لحساب ربحية المشروع لاحقًا.</span>
        </div>
      </div>
    </Modal>
  );
}

/* ========================================================================== */

function BreakdownModal({
  from,
  to,
  onClose,
}: {
  from: string;
  to: string;
  onClose: () => void;
}) {
  const data = useQuery({
    queryKey: ['expenses', 'by-account', from, to],
    queryFn: () =>
      api<{
        rows: Array<{
          accountId: string;
          code: string;
          name: string;
          count: number;
          amount: number;
          share: number;
        }>;
        total: number;
      }>(`/finance/expenses/by-account${qs({ from: from || undefined, to: to || undefined })}`),
  });

  return (
    <Modal
      title="توزيع المصروفات بالحساب"
      onClose={onClose}
      wide
      footer={
        <button className="btn btn-primary" onClick={onClose}>
          إغلاق
        </button>
      }
    >
      {data.isPending && <div className="skeleton" style={{ height: 240 }} />}

      {data.data && (
        <>
          <p className="small mute mb-4">
            إجمالي {money(data.data.total)} موزَّعة على {plural(data.data.rows.length, WORDS.account)}.
          </p>

          {/* سقفٌ بعدد الصفوف لا بارتفاع الشاشة: الجدول داخل نافذة، ورأسه يلتصق بأعلاها. */}
          <div className="table-wrap sticky-head rows-cap" style={{ ['--rows' as string]: 10 }}>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>الكود</th>
                  <th>الحساب</th>
                  <th style={{ width: 80 }}>العدد</th>
                  <th style={{ width: 130 }}>القيمة</th>
                  <th style={{ width: 180 }}>النسبة</th>
                </tr>
              </thead>
              <tbody>
                {data.data.rows.map((r) => (
                  <tr key={r.accountId}>
                    <td className="num cell-mute">{r.code}</td>
                    <td className="cell-strong">{r.name}</td>
                    <td className="num cell-mute">{r.count}</td>
                    <td className="num">{money(r.amount)}</td>
                    <td>
                      <div className="row" style={{ gap: 8 }}>
                        {/*
                          الشريط يجعل المقارنة بين الحسابات فورية. الرقم وحده
                          يحتاج قراءة كل سطر ومقارنته بغيره ذهنيًا.
                        */}
                        <div className="bar-track">
                          <div className="bar-fill" style={{ width: `${r.share}%` }} />
                        </div>
                        <span className="tiny mute num">{r.share}٪</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.data.rows.length === 0 && (
            <EmptyState title="لا مصروفات في هذه الفترة" text="غيّر النطاق الزمني من الشاشة." />
          )}
        </>
      )}
    </Modal>
  );
}
